import { toast } from 'react-toastify';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { api } from '~/services/api';
import history from '~/services/history';
import { ApplicationState } from '~/store';
import { ResponseProps } from '../../../propsGlobal/responseProps';
import { categoriaRequest } from '../common/actions';
import {
  companyFailure,
  companyRequest,
  companySuccess,
  preSignFailure,
  preSignInRequest,
  preSignInSuccess,
  requestPwdFailure,
  requestPwdRequest,
  requestPwdSuccess,
  signedFailure,
  signedRequest,
  signedSuccess,
  signFailure,
  signInRequest,
  signInSuccess,
  signOutFailure,
  signOutRequest,
  signOutSuccess,
} from './actions';
import { AuthTypes } from './types';

function* preSignIn({ payload }: ReturnType<typeof preSignInRequest>) {
  const { identificationKey, ownershipType, countryCompanyId, recaptcha } =
    payload.preSignIn;

  const data = JSON.stringify({
    identification_key: identificationKey,
    ownership_type: ownershipType,
    country_company_id: countryCompanyId,
  });

  try {
    const response: ResponseProps = yield call(
      api.post,
      '/v1/titulares/login',
      data,
      {
        headers: {
          'g-recaptcha-response': recaptcha,
          'content-type': 'application/json',
          Authorization: '',
        },
      },
    );

    const user = response.data.data.body;

    yield put(preSignInSuccess(user));
  } catch (error) {
    const message = error.response.data.message;

    if (message?.includes('Senha bloqueada')) {
      toast.error(
        `Seu CPF e Categoria foram bloqueados por um período de 24h, devido ao
        excesso de tentativas erradas. Após aguardar esse período, inicie um novo
        processo de geração de senha para obter o acesso ao Portal LGPD.`,
      );
      yield put(preSignFailure());
      return;
    }

    if (message?.includes('Senha expirada')) {
      toast.error(
        'Senha expirada. Por favor, repita o processo de geração de senha.',
      );
      yield put(preSignFailure());
      return;
    }

    toast.error(
      'CPF inválido ou inexistente na base de dados para a categoria selecionada',
    );
    yield put(preSignFailure());
  }
}

function* requestPwd({ payload }: ReturnType<typeof requestPwdRequest>) {
  const {
    identificationKey,
    ownershipType,
    countryCompanyId,
    emailIndex,
    recaptcha,
  } = payload.preSignIn;

  const data = JSON.stringify({
    identification_key: identificationKey,
    ownership_type: ownershipType,
    country_company_id: countryCompanyId,
  });

  try {
    yield call(api.post, '/v1/titulares/email', data, {
      headers: {
        'email-index': emailIndex,
        'g-recaptcha-response': recaptcha,
        'content-type': 'application/json',
        Authorization: '',
      },
    });

    yield put(requestPwdSuccess());
    toast.success(
      'Sua senha de acesso foi enviada. Verifique a sua caixa do correio eletrônico',
    );
  } catch (error) {
    toast.error('Identificação inválida');
    yield put(requestPwdFailure());
  }
}

function* signIn({ payload }: ReturnType<typeof signInRequest>) {
  const { company } = yield select((state: ApplicationState) => state.auth);
  const { identificationKey, ownershipType, countryCompanyId, pwd, recaptcha } =
    payload.signIn;

  const data = JSON.stringify({
    identification_key: identificationKey,
    ownership_type: ownershipType,
    country_company_id: countryCompanyId,
    password: pwd,
  });

  try {
    const response: ResponseProps = yield call(
      api.post,
      '/v1/titulares/auth',
      data,
      {
        headers: {
          'content-type': 'application/json',
          'g-recaptcha-response': recaptcha,
          Authorization: '',
        },
      },
    );

    const token = response.data.access_token;

    api.defaults.headers.Authorization = `Bearer ${token}`;
    yield put(signInSuccess(token));
    window.location.pathname = `/${company.path_alias_nm}/home`;
  } catch (error) {
    const message = error.response.data.message;

    if (message?.includes('Senha bloqueada')) {
      toast.error(
        `Seu CPF e Categoria foram bloqueados por um período de 24h, devido ao
        excesso de tentativas erradas. Após aguardar esse período, inicie um novo
        processo de geração de senha para obter o acesso ao Portal LGPD.`,
      );
      yield put(signFailure());
      return;
    }

    if (message?.includes('Senha expirada')) {
      toast.error(
        'Senha expirada. Por favor, repita o processo de geração de senha.',
      );
      yield put(signFailure());
      return;
    }

    toast.error(
      'Senha inválida. Informe a senha recebida no seu endereço de e-mail.',
    );

    yield put(signFailure());
  }
}

function* signOut({ payload }: ReturnType<typeof signOutRequest>) {
  const { company } = yield select((state: ApplicationState) => state.auth);

  try {
    yield call(api.post, '/v1/titulares/logout');

    yield put(signOutSuccess());
    window.location.pathname = `/${company.path_alias_nm}`;
  } catch (error) {
    yield put(signOutFailure());
  }
}

function* signed({ payload }: ReturnType<typeof signedRequest>) {
  const { company } = yield select((state: ApplicationState) => state.auth);
  const { token } = yield select(state => state.auth);

  try {
    const response: ResponseProps = yield call(
      api.post,
      '/v1/titulares/token',
      null,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    const data = {
      infoHeader: response.data,
      token: token,
    };

    yield put(signedSuccess(data));
  } catch (error) {
    window.location.pathname = `/${company.path_alias_nm}`;
    yield put(signedFailure());
  }
}

function* company({ payload }: ReturnType<typeof companyRequest>) {
  try {
    const response: ResponseProps = yield call(
      api.get,
      `/v1/company/alias?company=${payload.company}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: '',
        },
      },
    );

    yield put(categoriaRequest(response.data?.country_company_id));

    yield put(companySuccess(response.data));
  } catch (error) {
    history.push('/helpe');
    yield put(companyFailure());
  }
}

export function setToken(payload: any) {
  if (!payload) return;

  if (!!payload?.payload?.auth?.token) {
    const { token } = payload.payload.auth;

    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }
}

export default all([
  takeLatest('persist/REHYDRATE', setToken),
  takeLatest(AuthTypes.SIGNED_REQUEST, signed),
  takeLatest(AuthTypes.PRE_SIGN_IN_REQUEST, preSignIn),
  takeLatest(AuthTypes.REQUEST_PWD_SIGN_IN_REQUEST, requestPwd),
  takeLatest(AuthTypes.SIGN_IN_REQUEST, signIn),
  takeLatest(AuthTypes.SIGNOUT_REQUEST, signOut),
  takeLatest(AuthTypes.COMPANY_REQUEST, company),
]);
